// ======================================================================
// File: frontend/src/modules/ppe/pages/procurement/indent/form/IndentItemDrawer.js
// Module: PPE → Procurement → Indent
//
// Purpose:
// - Add / Edit Indent Line Items
// - Process Master–style right-side drawer
// - NO ERPSideDrawer usage
//
// ======================================================================

import React, { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";

import indentService from "../services/indentService";

// MATERIAL SEARCH SERVICE
import {
  searchRawMaterials,
  searchBoughtOutItems,
} from "../services/materialService";

export default function IndentItemDrawer({
  open,
  indentId,
  record,
  onClose,
  onSaved,
}) {
  const isEdit = Boolean(record);

  // --------------------------------------------------
  // FORM STATE
  // --------------------------------------------------
  const [form, setForm] = useState({
    material_type: "",
    material_id: "",
    material_name: "",
    uom: "",        // FK value (stored)
    uom_code: "",   // ✅ 2026-02-21 FIX: display value
    required_qty: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // --------------------------------------------------
  // INIT EDIT
  // --------------------------------------------------
  useEffect(() => {
    if (record) {
      setForm({
        // ✅ 2026-02-21 FIX:
        // Normalize material_type coming from backend
        material_type:
          record.material_type === "RAW_MATERIAL"
            ? "RAW"
            : record.material_type,

        material_id: record.material_id,
        material_name: record.material_name,

        // FK stored value
        uom: record.uom,

        // ✅ 2026-02-21 FIX:
        // Use display UOM if available
        uom_code: record.uom_code || record.uom || "",

        required_qty: record.required_qty,
      });
    } else {
      setForm({
        material_type: "",
        material_id: "",
        material_name: "",
        uom: "",
        uom_code: "",
        required_qty: "",
      });
    }
  }, [record]);

  if (!open) return null;

  // --------------------------------------------------
  // MATERIAL SEARCH LOADER
  // --------------------------------------------------
  const loadMaterials = async (inputValue) => {
    if (!form.material_type) return [];

    try {
      // ✅ 2026-02-21 FIX:
      // Correct enum alignment with backend
      if (form.material_type === "RAW") {
        return await searchRawMaterials(inputValue || "");
      }

      if (form.material_type === "BOUGHT_OUT") {
        return await searchBoughtOutItems(inputValue || "");
      }
    } catch (err) {
      console.error("Material search error:", err);
      return [];
    }

    return [];
  };

  // --------------------------------------------------
  // CHANGE HANDLER
  // --------------------------------------------------
  const onChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  };

  // --------------------------------------------------
  // SAVE
  // --------------------------------------------------
  const handleSave = async () => {
    setError(null);

    if (!form.material_type) {
      setError("Material type is required");
      return;
    }

    if (!form.material_id) {
      setError("Material is required");
      return;
    }

    if (!form.required_qty || form.required_qty <= 0) {
      setError("Quantity must be greater than zero");
      return;
    }

    const payload = {
      material_type: form.material_type,
      material_id: form.material_id,
      required_qty: Number(form.required_qty),
      uom: form.uom, // FK saved
    };

    try {
      setSaving(true);

      if (isEdit) {
        await indentService.updateIndentLine(record.id, payload);
      } else {
        await indentService.addIndentLine(indentId, payload);
      }

      onSaved?.();
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to save item"
      );
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------------------------
  // UI
  // --------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        width: 520,
        height: "100vh",
        background: "#fff",
        boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          padding: 16,
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h6 style={{ margin: 0 }}>
          {isEdit ? "Edit Indent Item" : "Add Indent Item"}
        </h6>

        <button
          className="btn btn-sm btn-light"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {/* BODY */}
      <div style={{ padding: 16, flex: 1 }}>
        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {/* MATERIAL TYPE */}
        <div className="mb-3">
          <label className="form-label">Material Type</label>
          <select
            className="form-select"
            value={form.material_type}
            onChange={(e) =>
              setForm({
                material_type: e.target.value,
                material_id: "",
                material_name: "",
                uom: "",
                uom_code: "", // ✅ 2026-02-21 reset display
                //required_qty: form.required_qty,
                required_qty: "",
              })
            }
          >
            <option value="">Select material type</option>

            {/* ✅ 2026-02-21 FIX: enum aligned with backend */}
            <option value="RAW">Raw Material</option>
            <option value="BOUGHT_OUT">Bought Out</option>
          </select>
        </div>

        {/* MATERIAL */}
        <div className="mb-3">
          <label className="form-label">Material</label>

          <AsyncSelect
            cacheOptions
            defaultOptions
            loadOptions={loadMaterials}
            isDisabled={!form.material_type}
            placeholder={
              form.material_type
                ? "Search material..."
                : "Select material type first"
            }

            getOptionValue={(opt) => opt.value}
            getOptionLabel={(opt) => opt.label}

            // ✅ 2026-02-21 FIX: correct edit binding
            value={
              form.material_id
                ? {
                    value: form.material_id,
                    label: `${form.material_name} - ${form.uom_code || ""}`,
                  }
                : null
            }

            onChange={(opt) => {
              if (!opt) return;

              setForm((p) => ({
                ...p,
                material_id: opt.value,
                material_name: opt.label,

                // FK stored
                uom: opt.uom,

                // ✅ display value
                uom_code: opt.uom_code || opt.uom || "",
                required_qty: "",
              }));
            }}
          />
        </div>

        {/* UOM */}
        <div className="mb-3">
          <label className="form-label">UOM</label>
          <input
            className="form-control"
            value={form.uom_code}
            readOnly
          />
        </div>

        {/* QUANTITY */}
        <div className="mb-3">
          <label className="form-label">Required Quantity</label>
          <input
            type="number"
            className="form-control"
            value={form.required_qty}
            onChange={(e) =>
              onChange("required_qty", e.target.value)
            }
          />
        </div>
      </div>

      {/* FOOTER */}
      <div
        style={{
          padding: 16,
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
        }}
      >
        <button
          className="btn btn-outline-secondary"
          onClick={onClose}
        >
          Cancel
        </button>

        <button
          className="btn btn-primary"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}